import React, { useCallback, useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { errText } from "../biz/i18n";
import { Card, Empty, btnMain, btnSoft, inp } from "../biz/ui";
import { useLabT } from "./i18n";

const TONE = { approved: "bg-[#E7F1E4] text-[#2F6B2A]", pending: "bg-[#FFF4DA] text-[#8A6212]", rejected: "bg-[#F6E3DF] text-[#A33A2B]", suspended: "bg-[#EFEAE2] text-[#6B645A]" };
const ACTIONS = { pending: [["approved", "approve"], ["rejected", "reject"]], approved: [["suspended", "suspend"], ["rejected", "revoke_access"]],
  rejected: [["approved", "approve"]], suspended: [["approved", "reactivate"], ["rejected", "revoke_access"]] };

const Answer = ({ a, lang }) => (
  <div className="text-sm">
    <p className="text-xs text-[#8A8173]">{lang === "ar" ? a.q_ar : a.q_en}</p>
    <p>{a.text || (a.selected || []).map((s) => (lang === "ar" ? s.ar : s.en)).join("، ")}</p>
    {a.comment && <p className="text-xs text-[#6B645A] italic">“{a.comment}”</p>}
  </div>
);

const Row = ({ p, t, lang, onSet }) => {
  const [open, setOpen] = useState(p.status === "pending");
  return (
    <Card testid={`lab-part-${p.email}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-start">
        <div className="flex items-center gap-2 flex-wrap"><b>{p.full_name || p.email}</b>
          <span className={`text-xs rounded-full px-2.5 py-1 ${TONE[p.status]}`} data-testid={`lab-part-status-${p.email}`}>{t(`p_${p.status}`)}</span></div>
        <p className="text-xs text-[#8A8173] mt-1" dir="ltr">{p.email}{p.phone ? ` · ${p.phone}` : ""}{p.city ? ` · ${p.city}, ${p.country}` : ""}</p>
      </button>
      {open && (
        <div className="mt-3 space-y-3 border-t border-[#EEE5D8] pt-3">
          {Object.values(p.answers || {}).length > 0 && <div className="space-y-2"><p className="text-xs font-semibold">{t("answers")}</p>{Object.entries(p.answers).map(([k, a]) => <Answer key={k} a={a} lang={lang} />)}</div>}
          <p className="text-xs text-[#6B645A]"><b>{t("purchases")}:</b> <span dir="ltr">{p.orders.length ? p.orders.map((o) => `${o.order_number} (${o.codes.join(", ")})`).join(" · ") : t("none_yet")}</span></p>
          <p className="text-xs text-[#6B645A]"><b>{t("evaluated")}:</b> <span dir="ltr">{p.evaluated.length ? p.evaluated.join(", ") : t("none_yet")}</span></p>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        {(ACTIONS[p.status] || []).map(([st, label]) => (
          <button key={st + label} onClick={() => onSet(p, st)} className={st === "approved" ? btnMain : btnSoft} data-testid={`lab-part-${label}-${p.email}`}>{t(label)}</button>
        ))}
      </div>
    </Card>
  );
};

export function LabParticipants() {
  const { t, lang } = useLabT();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [email, setEmail] = useState("");
  const load = useCallback(() => api.get("/admin/lab/participants").then((r) => setRows(r.data)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn) => { try { await fn(); toast.success(t("saved")); load(); } catch (e) { toast.error(errText(e)); } };
  const onSet = (p, status) => (status === "approved" || window.confirm(`${t(`p_${status}`)}?`)) && run(() => api.put(`/admin/lab/participants/${p.id}`, { status }));
  if (!rows) return null;
  const shown = rows.filter((r) => filter === "all" || r.status === filter);
  const count = (s) => rows.filter((r) => r.status === s).length;
  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {["all", "pending", "approved", "suspended", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`lab-part-filter-${s}`}
            className={`rounded-full px-3.5 py-2 text-xs whitespace-nowrap ${filter === s ? "bg-[#9A7445] text-white" : "bg-white border border-[#EEE5D8]"}`}>
            {s === "all" ? t("all") : t(`p_${s}`)} {s !== "all" && `(${count(s)})`}
          </button>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); run(async () => { await api.post("/admin/lab/participants", { email }); setEmail(""); }); }} className="flex gap-2">
        <input type="email" required dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className={inp} data-testid="lab-part-add-email" />
        <button type="submit" className={`${btnSoft} whitespace-nowrap`} data-testid="lab-part-add"><UserPlus size={15} />{t("add_participant")}</button>
      </form>
      {!shown.length && <Empty>{t("no_items")}</Empty>}
      {shown.map((p) => <Row key={p.id} p={p} t={t} lang={lang} onSet={onSet} />)}
    </div>
  );
}
